import React, { useEffect, useState, useContext } from "react";
import {
  Computer,
  Pending,
  CheckCircle,
  ErrorOutline,
} from "@mui/icons-material";
import { Card, CardContent, Typography, Box, useTheme, Grid } from "@mui/material";
import { motion } from "framer-motion";
import AuthContext from "../../AuthContext";
import { getDevicesCards } from "../../apis/axios";

const StatCards = () => {
  const auth = useContext(AuthContext);
  const theme = useTheme();
  const [devicesData, setDevicesData] = useState(null);

  const fetchDevicesData = async () => {
    try {
      const response = await getDevicesCards({ company_id: auth.admin });
      setDevicesData(response.data);
    } catch (error) {
      console.error("Error fetching devices data:", error);
    }
  };

  useEffect(() => {
    fetchDevicesData();
  }, [auth.admin]);

  const isLight = theme.palette.mode === "light";
  const bgColor = isLight ? "#FFFFFF" : "#1E1E2F";
  const textColor = isLight ? "#1E1E2F" : "#F8FAFC";
  const subText = isLight ? "#6B7280" : "#CBD5E1";

  const lavender = "#C4B5FD";
  const purple = "#7C3AED";
  const softBlue = "#A5B4FC";
  const gradient = `linear-gradient(135deg, ${lavender}30, ${softBlue}40)`;

  const cardData = [
    {
      title: "Total Devices",
      value: devicesData ? devicesData.devices.totalDevices : "-",
      percentage: devicesData
        ? `+${devicesData.devices.monthDevices} this month`
        : "-",
      icon: <Computer fontSize="large" />,
      accent: purple,
    },
    {
      title: "Pending Wipes",
      value: devicesData ? devicesData.wipesPending.total : "-",
      percentage: devicesData
        ? `+${devicesData.wipesPending.month} this month`
        : "-",
      icon: <Pending fontSize="large" />,
      accent: "#FACC15", // yellow accent
    },
    {
      title: "Completed Wipes",
      value: devicesData ? devicesData.totalWipes.total : "-",
      percentage: devicesData
        ? `+${devicesData.totalWipes.month} this month`
        : "-",
      icon: <CheckCircle fontSize="large" />,
      accent: "#22C55E", // green accent
    },
    {
      title: "Failed Wipes",
      value: devicesData ? devicesData.failedWipes.total : "-",
      percentage: devicesData
        ? `+${devicesData.failedWipes.month} this month`
        : "-",
      icon: <ErrorOutline fontSize="large" />,
      accent: "#EF4444", // red accent
    },
  ];

  return (
    <Grid container spacing={3} mb={3}>
      {cardData.map((card, index) => (
        <Grid item size={{xs:12, sm:6 ,md:3}} key={index}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1, duration: 0.4 }}
          >
            <Card
              sx={{
                background: bgColor,
                color: textColor,
                borderRadius: 3,
                boxShadow: isLight
                  ? "0 4px 10px rgba(0,0,0,0.1)"
                  : "0 4px 20px rgba(0,0,0,0.5)",
                overflow: "hidden",
                transition: "all 0.3s ease",
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background: gradient,
                  opacity: 0,
                  transition: "opacity 0.4s ease",
                  zIndex: 0,
                },
                "&:hover::before": { opacity: 1 },
                "&:hover": {
                  transform: "translateY(-5px)",
                },
              }}
            >
              <CardContent
                sx={{
                  position: "relative",
                  zIndex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 3,
                }}
              >
                {/* Left side */}
                <Box>
                  <Typography
                    variant="subtitle3"
                    sx={{
                      color: subText,
                      fontWeight: 600,
                      letterSpacing: 0.3,
                      fontFamily: "Nunito Sans, sans-serif",
                    }}
                  >
                    {card.title}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: textColor,
                      mt: 1,
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    {card.value}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: card.accent,
                      fontWeight: 500,
                      mt: 0.5,
                    }}
                  >
                    {card.percentage}
                  </Typography>
                </Box>

                {/* Right side icon */}
                <motion.div
                  whileHover={{ rotate: 15, scale: 1.1 }}
                  transition={{ type: "spring", stiffness: 200 }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      background: `linear-gradient(135deg, ${card.accent}33, ${lavender}66)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: card.accent,
                    }}
                  >
                    {card.icon}
                  </Box>
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      ))}
    </Grid>
  );
};

export default StatCards;
